/**
 * A lesson's match plays for the life of the lesson (booking-contracts.md
 * 15; 3 Nov). The processor stored a 7 day signed URL in playerVideoUrl, so
 * a match stopped playing a week after it was uploaded. A stored URL that
 * doesn't expire plays as it is; a signed one is asked for again on read
 * (getLiveMatchVideo), once per lesson per page, and that URL lasts. When
 * the call fails, the stored URL is tried as before.
 */

/** Whether a stored URL expires: a V4 or V2 signed URL. */
export function expires(url: string | null | undefined): boolean {
  return !!url && /[?&](X-Goog-Expires|X-Goog-Signature|Expires)=/.test(url);
}

/** What a lesson's match is stored as. */
export interface StoredMatch {
  id: string;
  playerVideoUrl?: string | null;
  match?: { videoUrl?: string | null } | null;
}

/** The stored URL a lesson's match would play. */
export function storedMatchUrl(b: StoredMatch | null | undefined): string | null {
  return b?.match?.videoUrl || b?.playerVideoUrl || null;
}

/**
 * The resolver: one call per lesson (and stored URL) for the life of the page.
 * @param call Asks the server for the lesson's lasting URL.
 */
export function matchVideoResolver(call: (bookingId: string) => Promise<string>) {
  const asked = new Map<string, Promise<string>>();
  return (b: StoredMatch): Promise<string | null> => {
    const stored = storedMatchUrl(b);
    if (!stored) return Promise.resolve(null);
    if (!expires(stored)) return Promise.resolve(stored);
    const key = `${b.id}|${stored}`;
    let p = asked.get(key);
    if (!p) {
      p = call(b.id).catch((err) => {
        asked.delete(key);
        console.warn('[match] lasting URL not found; the stored one is tried', err);
        return stored;
      });
      asked.set(key, p);
    }
    return p;
  };
}
